<template>
    <div>
        <div
            v-if="authUserProfile"
            class="user-desktop-action-menu-container"
            @mouseleave="userMenuToggleDebounced(false)"
        >
            <CoreShapedImage
                v-if="menuItems && authUserProfile"
                :size="40"
                :image="imageUrl"
                :altText="userFullName"
                :class="{ 'trigger-active': userMenuTriggerHovered }"
                :hasSubstate="true"
                data-testid="user-avatar"
                @mouseover.native="userMenuToggleDebounced(true)"
            />
            <CoreActionMenu
                v-show="isUserDropdownContentShown"
                :title="userFullName"
                class="user-icon-menu"
                data-testid="user-actions-menu"
                @mouseenter.native="enterDropdownContent"
                @mouseleave.native="userMenuToggleDebounced(false)"
            >
                <CoreMenuItem
                    v-for="item in items"
                    :key="item.string"
                    class="core-user-menu-list-item"
                    :item="item"
                    @click="handleClick($event, item.onClick)"
                />
            </CoreActionMenu>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Emit, Prop, Mixins } from 'vue-property-decorator';
    import { TopNavUserInterface } from '../../interfaces';
    import CoreMenuItem from './CoreMenuItem.vue';
    import TopNavigationUserProfileContentLoader from '@/common/components/content-loaders/TopNavigationUserProfileContentLoader.vue';
    import vClickOutside from 'v-click-outside';
    import CoreActionDropdown from '@/common/components/menu/CoreActionDropdown/CoreActionDropdown.vue';
    import CoreActionMenu from '@/common/components/menu/CoreActionDropdown/CoreActionMenu.vue';
    import CoreShapedImage from '@/common/components/icon/CoreShapedImage.vue';
    import { UserProfileDetailsStoreMixin } from '@/common/store/user-details/store';
    import { MobileModeStoreMixin } from '@/common/store/mobile-mode';
    import { debounce } from 'lodash-es';
    import { NotificationsMappedStore } from '@/notifications/store';
    import '@eloomi/icons/alerts-info/alerts-info-notification';
    import { TranslationMixin } from '@/common/mixins';

    @Component({
        components: {
            CoreMenuItem,
            TopNavigationUserProfileContentLoader,
            CoreActionDropdown,
            CoreActionMenu,
            CoreShapedImage,
        },
        directives: {
            clickOutside: vClickOutside.directive,
        },
    })
    export default class CoreUserMenu extends Mixins(
        MobileModeStoreMixin,
        UserProfileDetailsStoreMixin,
        NotificationsMappedStore,
        TranslationMixin
    ) {
        @Prop() public menuItems!: TopNavUserInterface;

        public isShow = false;
        public userMenuShow = false;
        public userMenuTriggerHovered = false;

        public userMenuToggleDebounced = debounce(this.userMenuToggle, 200);

        public get isUserDropdownContentShown() {
            return this.userMenuShow || this.userMenuTriggerHovered;
        }

        public created() {
            this.loadUserProfile();
        }

        enterDropdownContent() {
            this.userMenuTriggerHovered = false;
        }

        public async userMenuToggle(status: boolean) {
            if (status === true) {
                this.userMenuTriggerHovered = true;
                setTimeout(() => (this.userMenuTriggerHovered = false), 2000);
            }
            this.userMenuShow = status;
        }

        @Emit('open')
        public emitOpen() {
            return;
        }

        @Emit('close')
        public emitClose() {
            return;
        }

        public get items() {
            return this.menuItems ? Object.values(this.menuItems.subnav || {}) : [];
        }

        public get imageUrl() {
            return this.menuItems.image_url ? this.menuItems.image_url : '';
        }

        public get userFullName(): string {
            return this.menuItems.first_name && this.menuItems.last_name
                ? `${this.menuItems.first_name} ${this.menuItems.last_name}`
                : this.authUserFullName;
        }

        public get authUserFullName(): string {
            return `${this.authUserProfile?.firstName} ${this.authUserProfile?.lastName}`;
        }

        public handleClick(event: Event, itemClick?: () => void) {
            if (typeof itemClick === 'function') {
                itemClick();
                event.preventDefault();
            }
        }
    }
</script>
<style lang="less" scoped>
    .user-icon-menu {
        position: absolute;
        top: 75px;
        right: 49px;
    }
    .trigger-active {
        position: relative;
        &::before {
            position: absolute;
            top: 50%;
            right: 0;
            width: 600%;
            height: 200%;
            transform: translateY(-50%);
            content: '';
        }
    }
    .user-desktop-action-menu-container {
        padding: @spacing-24 0;
    }
</style>
